import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { Box, TextButton } from '@wix/design-system';
import { ArrowDownRightSmall } from '@wix/wix-ui-icons-common';
import useReplyToReview from './useReplyToReview';
import ReviewReplyModal from '@components/Template/TemplateReviews/ReviewReplyModal/ReviewReplyModal';
import ReplyForm from './ReplyForm';
import ReplyReview from '../ReplyReview';
import ReplyActionButtons from '../ReviewReplyDialogue/ReplyActionButtons';
import DeleteReviewReplyModal from '../DeleteReviewReplyModal';
import { IWriteReplyToReviewProps } from '../types';

export const writeReplyReviewDataHooks = {
  container: 'write-reply-review-container',
  replyLink: 'write-reply-review-link',
};

const WriteReplyReview = ({
  review,
  writeReplyToReview,
  deleteReply,
  isMobileView,
  isUsingMobileModal,
  handleBiGenericClick,
  hasActionMenu,
  replyStyleProps,
}: IWriteReplyToReviewProps) => {
  const [showForm, setShowForm] = useState<boolean>(false);
  const [isShowingDeleteModal, setIsShowingDeleteModal] =
    useState<boolean>(false);
  const [focusOnReview, setFocusOnReview] = useState<boolean>(false);
  const isMobileModalOpen =
    isMobileView && isUsingMobileModal && (showForm || focusOnReview);
  const onClose = useCallback(() => setShowForm(false), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setFocusOnReview(params?.get('reviewId') === review.id);
  }, []);

  const replyUtils = useReplyToReview({
    writeReplyToReview,
    review,
    onClose,
  });

  const reply = review.reply;

  if (reply && !showForm) {
    return (
      <Box dataHook={writeReplyReviewDataHooks.container}>
        <ReplyReview
          reply={reply}
          replyTitleKey={'marketplace.template-page.reply-title'}
          actionsNode={
            hasActionMenu ? (
              <ReplyActionButtons
                isMobile={isMobileView}
                editOnClick={() => setShowForm(true)}
                deleteOnClick={() => setIsShowingDeleteModal(true)}
              />
            ) : null
          }
        />
        <DeleteReviewReplyModal
          isOpen={isShowingDeleteModal}
          onDelete={() => {
            deleteReply?.(review.id!);
            setIsShowingDeleteModal(false);
          }}
          onClose={() => setIsShowingDeleteModal(false)}
          isMobile={isMobileView}
        />
      </Box>
    );
  }

  if (!showForm && !focusOnReview) {
    return (
      <WriteReplyReviewContainer>
        <ShowReplyFormButton
          onClick={() => {
            handleBiGenericClick &&
              handleBiGenericClick({ clickType: 'Reply on review' });
            setShowForm(true);
          }}
        />
      </WriteReplyReviewContainer>
    );
  }

  if (isMobileModalOpen) {
    return (
      <WriteReplyReviewContainer>
        <ReviewReplyModal
          review={review}
          isOpen={showForm || focusOnReview}
          onClose={() => {
            handleBiGenericClick &&
              handleBiGenericClick({ clickType: 'Close reply on review' });
            onClose();
          }}
          onCancel={() => {
            handleBiGenericClick &&
              handleBiGenericClick({ clickType: 'Cancel reply on review' });
            onClose();
          }}
          replyUtils={replyUtils}
          {...replyStyleProps}
        />
      </WriteReplyReviewContainer>
    );
  }

  return (
    <WriteReplyReviewContainer>
      <ReplyForm
        replyUtils={replyUtils}
        onClose={() => {
          handleBiGenericClick &&
            handleBiGenericClick({ clickType: 'Cancel reply on review' });
          onClose();
        }}
        autoFocus={focusOnReview}
        {...replyStyleProps}
      />
    </WriteReplyReviewContainer>
  );
};

const WriteReplyReviewContainer: FC<{ children: JSX.Element }> = ({
  children,
}) => {
  return (
    <Box width="100%" dataHook={writeReplyReviewDataHooks.container}>
      <Box width="SP3">
        <ArrowDownRightSmall />
      </Box>
      {children}
    </Box>
  );
};

const ShowReplyFormButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <Box display="block" marginLeft="SP1">
      <Box display="block" marginBottom="SP1">
        <TextButton
          skin="dark"
          dataHook={writeReplyReviewDataHooks.replyLink}
          underline="always"
          size="small"
          weight="thin"
          onClick={onClick}
        >
          {t('profile-page-review-write-reply-link')}
        </TextButton>
      </Box>
    </Box>
  );
};

export default WriteReplyReview;
